import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, API } from "../lib/api";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import ShareButtons from "../components/ShareButtons";
import { ArrowUpRight, ChevronLeft, ChevronRight, Luggage, Plane, Calendar, Hotel, Lock, Wifi } from "lucide-react";
import RichTextRenderer from "../components/RichTextRenderer";
import { sym, CURRENCY_SYMBOLS } from "../lib/currencies";

const STOPS_LABELS = { nonstop: "Nonstop", "1 stop": "1 Stop", "1stop": "1 Stop", "2+ stops": "2+ Stops", "2stops": "2+ Stops" };
const formatStops = (s) => STOPS_LABELS[(s || "").toLowerCase()] || s || "";

// For hotel deals — link to flight search rather than another hotel search
const FLIGHT_SEARCH_URL = "https://thebetterfare.com/flight-request";

// Booking.com affiliate URL for flight deals
const BOOKING_AFFILIATE_ID = "AFFILIATE_ID_HERE";
const bookingSearchUrl = (destination) =>
  `https://www.booking.com/search.html?ss=${encodeURIComponent(destination)}&aid=${BOOKING_AFFILIATE_ID}`;

const TRIP_ESIM_URL = "https://www.trip.com/t/s1gbNdelSU2";

export default function DealPage() {
  const slug = useParams()["*"];
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState("");
  const [activePhoto, setActivePhoto] = useState(0);
  const [related, setRelated] = useState([]);
  const touchStartX = useRef(null);

  useEffect(() => {
    setDeal(null);
    setActivePhoto(0);
    api.get(`/deals/${slug}`).then(
      (r) => {
        setDeal(r.data);
        const canonical = r.data.slug || r.data.id;
        if (canonical && canonical !== slug) {
          navigate(`/deal/${canonical}`, { replace: true });
        }
        api.get(`/deals/${r.data.id}/related`).then((rel) => setRelated(rel.data)).catch(() => {});
      },
      () => setError("File not found")
    );
  }, [slug, navigate]);

  if (error) {
    return (
      <div className="bg-[#111111] min-h-screen">
        <SiteHeader />
        <div className="max-w-3xl mx-auto py-32 text-center">
          <p className="eyebrow eyebrow-red">// 404</p>
          <h1 className="font-mono text-5xl mt-4 text-white uppercase">File not found</h1>
          <Link to="/" className="btn-rd mt-8 inline-flex">← BACK TO FEED</Link>
        </div>
      </div>
    );
  }
  if (!deal) {
    return (
      <div className="bg-[#111111] min-h-screen">
        <SiteHeader />
        <div className="max-w-3xl mx-auto py-32 animate-pulse" data-testid="deal-loading">
          <div className="h-[50vh] bg-[#1A1A1A]" />
          <div className="h-10 w-1/2 bg-[#1A1A1A] mt-8" />
        </div>
      </div>
    );
  }

  const dealType = deal.deal_type || "flight";
  const s = sym(deal.price_currency);
  const dealUrl = deal.deal_url || "";
  const clickUrl = (type) => `${API}/deals/${deal.id}/click?type=${type}`;

  const _TRIP_AFF = "Allianceid=4012514&SID=308550495&trip_sub1=&trip_sub3=D16678678";
  const _TRIP_BASE = `https://www.trip.com/flights/?${_TRIP_AFF}`;
  const _cap = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());
  const _fromCity = _cap(deal.from_city);
  const _toCity = _cap(deal.to_city);
  const _fromIata = (deal.from_iata || "").toUpperCase();
  const _toIata = ((deal.return_to_iata || deal.to_iata) || "").toUpperCase();
  const tripFlightUrl = (_fromIata && _toIata)
    ? `https://www.trip.com/flights/${_fromCity}-to-${_toCity}/tickets-${_fromIata}-${_toIata}/?flighttype=S&dcity=${_fromIata}&acity=${_toIata}&curr=EUR&${_TRIP_AFF}`
    : _TRIP_BASE;
  const tripHotelUrl = `https://www.trip.com/hotels/list?display=${encodeURIComponent(deal.to_city || "")}&${_TRIP_AFF}`;
  const tripPackageUrl = `https://www.trip.com/packages/list/${encodeURIComponent((deal.from_city || "").toLowerCase())}-to-${encodeURIComponent((deal.to_city || "").toLowerCase())}/?${_TRIP_AFF}`;

  const img = deal.image_url ||
    "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?w=1800&q=85&auto=format&fit=crop";
  // Normalize gallery — old deals store plain URL strings, new ones store { url, credit } objects.
  const allPhotoObjects = [
    { url: img, credit: deal.photo_credit || null },
    ...(deal.gallery || []).map(p => typeof p === "string" ? { url: p, credit: null } : p),
  ].filter(p => p.url);
  const allPhotos = allPhotoObjects.map(p => p.url);
  const hasGallery = allPhotos.length > 1;

  const currentCredit = allPhotoObjects[activePhoto]?.credit ?? null;
  const currentPhotoUrl = allPhotoObjects[activePhoto]?.url ?? null;

  const prevPhoto = () => setActivePhoto((p) => (p > 0 ? p - 1 : allPhotos.length - 1));
  const nextPhoto = () => setActivePhoto((p) => (p < allPhotos.length - 1 ? p + 1 : 0));

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) { dx < 0 ? nextPhoto() : prevPhoto(); }
    touchStartX.current = null;
  };
  const ogImage = `https://flightdeals-production-51a6.up.railway.app/api/og/deal/${deal.slug || deal.id}`;
  const canonical = `https://restricteddeals.com/deal/${deal.slug || deal.id}`;

  // Open-jaw / multi-city return leg (declared early — used in pageTitle, heroTitle and JSX)
  const returnFrom = deal.return_from_city || deal.return_from_airport || "";
  const returnTo = deal.return_to_city || deal.return_to_airport || deal.from_city || "";
  const _retKey = returnFrom.toUpperCase().split("(")[0].trim();
  const _toKey = (deal.to_city || deal.to_airport || "").toUpperCase().split("(")[0].trim();
  const isOpenJaw = Boolean(returnFrom && _retKey !== _toKey);
  const isMultiCity = deal.trip_type === "multi_city" || deal.trip_type === "multi";
  const flightRoute = (isOpenJaw || (isMultiCity && returnFrom))
    ? `${deal.from_city} → ${deal.to_city} / ${returnFrom} → ${returnTo}`
    : `${deal.from_city || ""} → ${deal.to_city || ""}`;

  // Cruise dep→dest headline (declared early — used in pageTitle, heroTitle and JSX)
  const cruiseDepDest = dealType === "cruise"
    ? (deal.departure_port && deal.destination_port
        ? `${deal.departure_port} → ${deal.destination_port}`
        : deal.departure_port || deal.destination_port || deal.cruise_route || deal.to_city || null)
    : null;

  // Type-specific SEO
  const pageTitle = dealType === "hotel"
    ? `${deal.star_rating ? `${deal.star_rating}★ ` : ""}${deal.hotel_name || deal.to_city} from ${s}${deal.price_eur}/night – Restricted Deals`
    : dealType === "cruise"
    ? `${deal.cruise_line || "Cruise"} – ${cruiseDepDest || deal.cruise_route || ""} from ${s}${deal.price_eur} p.p. – Restricted Deals`
    : dealType === "package"
    ? `${deal.from_city || ""} → ${deal.to_city || ""} package from ${s}${deal.price_eur} p.p. – Restricted Deals`
    : `Flights ${flightRoute} for ${s}${deal.price_eur} – Restricted Deals`;

  const metaDescription = dealType === "hotel"
    ? `${deal.hotel_name || deal.to_city} in ${deal.to_city} from ${s}${deal.price_eur}/night.${deal.board_type ? ` ${deal.board_type}.` : ""}${deal.checkin_period || deal.travel_window ? ` Available ${deal.checkin_period || deal.travel_window}.` : ""}`
    : dealType === "cruise"
    ? `${deal.cruise_line} cruise from ${s}${deal.price_eur} per person.${deal.duration_nights ? ` ${deal.duration_nights} nights.` : ""} ${deal.cruise_route || ""}.`
    : dealType === "package"
    ? `${deal.from_city} to ${deal.to_city} package from ${s}${deal.price_eur} per person. Includes: ${deal.package_includes || ""}. Travel ${deal.travel_window || ""}.`
    : `${deal.airline} from ${deal.from_city} to ${deal.to_city} from ${s}${deal.price_eur} roundtrip. Travel ${deal.travel_window}. ${formatStops(deal.stops)}. ${deal.baggage}.`;

  // JSON-LD schema
  const jsonLd = (() => {
    const currency = deal.price_currency || "EUR";
    const price = deal.price_eur;
    const dealUrl = `https://restricteddeals.com/deal/${deal.slug || deal.id}`;

    // Shared Offer extensions required by Google Merchant Center
    const offerExtras = {
      brand: { "@type": "Brand", name: "Restricted Deals" },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "NL",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "EUR" },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
        },
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "NL" },
      },
    };

    // Parse travel_window → { startDate, endDate }
    // Handles: "JUN - OCT 2026", "MAY 2026", "JUN 2026 - OCT 2026"
    const MONTHS = { JAN:1,FEB:2,MAR:3,APR:4,MAY:5,JUN:6,JUL:7,AUG:8,SEP:9,OCT:10,NOV:11,DEC:12 };
    const lastDayOf = (y, m) => new Date(y, m, 0).getDate(); // m is 1-indexed

    let startDate = deal.created_at ? deal.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const fallbackEnd = (() => {
      const d = deal.created_at ? new Date(deal.created_at) : new Date();
      d.setDate(d.getDate() + 90);
      return d.toISOString().slice(0, 10);
    })();
    let endDate = fallbackEnd;

    if (deal.travel_window) {
      const upper = deal.travel_window.toUpperCase();
      const monthHits = [...upper.matchAll(/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/g)];
      const yearHits  = [...upper.matchAll(/\b(\d{4})\b/g)];
      if (monthHits.length > 0 && yearHits.length > 0) {
        const y0 = parseInt(yearHits[0][1]);
        const y1 = parseInt(yearHits[yearHits.length - 1][1]);
        const m0 = MONTHS[monthHits[0][1]];
        const m1 = MONTHS[monthHits[monthHits.length - 1][1]];
        startDate = `${y0}-${String(m0).padStart(2, "0")}-01`;
        endDate   = `${y1}-${String(m1).padStart(2, "0")}-${String(lastDayOf(y1, m1)).padStart(2, "0")}`;
      }
    }

    // Performer: the operating company for each deal type
    const performerName =
      (dealType === "flight" || dealType === "package") ? deal.airline :
      dealType === "hotel"  ? (deal.hotel_name || null) :
      dealType === "cruise" ? deal.cruise_line : null;
    const performer = {
      "@type": "Organization",
      name: performerName || "Restricted Deals",
      ...(performerName ? {} : { url: "https://restricteddeals.com" }),
    };

    if (dealType === "hotel") {
      const schema = {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        name: deal.hotel_name || deal.to_city,
        address: { "@type": "PostalAddress", addressLocality: deal.to_city },
        performer,
        offers: {
          "@type": "Offer",
          price,
          priceCurrency: currency,
          availability: "https://schema.org/InStock",
          ...offerExtras,
        },
      };
      if (deal.star_rating) schema.starRating = { "@type": "Rating", ratingValue: deal.star_rating };
      return schema;
    }

    if (dealType === "cruise") {
      return {
        "@context": "https://schema.org",
        "@type": "Trip",
        name: [deal.cruise_line, deal.cruise_route].filter(Boolean).join(" – "),
        performer,
        offers: { "@type": "Offer", price, priceCurrency: currency, ...offerExtras },
      };
    }

    return {
      "@context": "https://schema.org",
      "@type": "Event",
      name: `${deal.from_city || ""} to ${deal.to_city || ""} for ${sym(currency)}${price}`,
      description: metaDescription,
      url: dealUrl,
      startDate,
      endDate,
      eventStatus: "https://schema.org/EventScheduled",
      location: {
        "@type": "Place",
        name: [deal.to_city, deal.to_country].filter(Boolean).join(", "),
        address: { "@type": "PostalAddress", addressLocality: deal.to_city },
      },
      organizer: {
        "@type": "Organization",
        name: "Restricted Deals",
        url: "https://restricteddeals.com",
      },
      performer,
      ...(deal.image_url ? { image: deal.image_url } : {}),
      offers: {
        "@type": "Offer",
        url: dealUrl,
        price,
        priceCurrency: currency,
        availability: "https://schema.org/InStock",
        ...(deal.created_at ? { validFrom: deal.created_at } : {}),
        ...offerExtras,
      },
    };
  })();

  // Hero heading
  const heroTitle = dealType === "hotel"
    ? (deal.hotel_name || deal.to_city)
    : dealType === "cruise"
    ? (deal.cruise_line || "Cruise")
    : flightRoute;

  // Cruise H1: "CRUISE LINE · SHIP · DEP → DEST" (short port names from parentheses)
  const shortPort = (port) => {
    if (!port) return "";
    const m = port.match(/\(([^)]+)\)/);
    return m ? m[1].trim() : port;
  };
  const cruiseH1 = (() => {
    if (dealType !== "cruise") return null;
    const parts = [deal.cruise_line || "Cruise"];
    if (deal.ship_name) parts.push(deal.ship_name);
    const dep = shortPort(deal.departure_port);
    const dest = shortPort(deal.destination_port || "");
    const route = dep && dest ? `${dep} → ${dest}` : dep || dest || deal.cruise_route || "";
    if (route) parts.push(route);
    return parts.join(" · ");
  })();

  // Region tag (first tag that is a known region)
  const ALL_REGIONS = ["Europe", "Asia", "Americas", "Africa", "Middle East", "Oceania"];
  const dealRegion = deal.tags?.find((t) => ALL_REGIONS.includes(t)) || null;

  // Breadcrumb
  const SITE = "https://restricteddeals.com";
  const typeLabel = { flight: "Flights", hotel: "Hotels", cruise: "Cruises", package: "Packages" }[dealType] || "Deals";
  const typeParam = dealType;
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",     item: SITE },
      { "@type": "ListItem", position: 2, name: "Deals",    item: `${SITE}/#feed` },
      { "@type": "ListItem", position: 3, name: typeLabel,  item: `${SITE}/?type=${typeParam}` },
      { "@type": "ListItem", position: 4, name: heroTitle },
    ],
  };

  // Hero subtitle
  const heroSub = dealType === "hotel"
    ? [deal.to_city, deal.to_country].filter(Boolean).join(", ") + (deal.checkin_period ? ` · ${deal.checkin_period}` : deal.travel_window ? ` · ${deal.travel_window}` : "")
    : dealType === "cruise"
    ? [
        cruiseDepDest,
        deal.duration_nights
          ? `${deal.duration_nights} nights from ${s}${deal.price_eur} p.p.`
          : `from ${s}${deal.price_eur} p.p.`,
        deal.travel_window,
      ].filter(Boolean).join(" · ")
    : (deal.travel_window || "");

  // CTA card pricing label
  const priceLabel = dealType === "hotel"
    ? deal.price_per_person
      ? `PER PERSON/NIGHT · ${deal.board_type || "ROOM RATE"}`
      : `PER ROOM/NIGHT · ${deal.board_type || "ROOM RATE"}`
    : dealType === "cruise"
    ? `PER PERSON · ${deal.cabin_type ? `${deal.cabin_type.toUpperCase()} CABIN` : "ALL FEES INCLUDED"}`
    : dealType === "package"
    ? `PER PERSON · ${deal.package_includes || "PACKAGE RATE"}`
    : `${{ round_trip: "ROUND TRIP", one_way: "ONE WAY", multi_city: "MULTI-CITY" }[deal.trip_type] || "ROUND TRIP"} · TAXES INCLUDED`;

  // Destination city used for the ground transport affiliate link
  const transferCity = dealType === "cruise" ? deal.departure_port : deal.to_city;

  // eSIM block: only show when there's a clear single destination
  const esimDestination = dealType === "cruise"
    ? (deal.destination_port || null)
    : deal.to_city;

  return (
    <div className="bg-[#111111]">
      <Helmet prioritizeSeoTags>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} data-rh="true" />
        <link rel="canonical" href={canonical} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Restricted Deals" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <SiteHeader />

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="bg-[#0B0B0B] border-b border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center gap-2 text-[10px] font-mono tracking-[0.18em] uppercase text-[#666]">
          <Link to="/" className="hover:text-[#CC1A1A] transition-colors">Home</Link>
          <span className="text-[#333]">›</span>
          <Link to="/#feed" className="hover:text-[#CC1A1A] transition-colors">Deals</Link>
          <span className="text-[#333]">›</span>
          <Link to={`/?type=${typeParam}`} className="hover:text-[#CC1A1A] transition-colors">{typeLabel}</Link>
          <span className="text-[#333]">›</span>
          <span className="text-[#9A8F83] truncate max-w-[180px]">{heroTitle}</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative" data-testid="deal-hero">
        <div
          className="relative h-[60vh] md:h-[75vh] max-h-[80vh] w-full overflow-hidden"
          onTouchStart={hasGallery ? onTouchStart : undefined}
          onTouchEnd={hasGallery ? onTouchEnd : undefined}
        >
          <img
            src={allPhotos[activePhoto]}
            alt={deal.hotel_name || deal.cruise_line || deal.to_city || "Deal photo"}
            className="w-full h-full object-cover opacity-85 transition-opacity duration-300"
            style={{ objectPosition: activePhoto === 0 ? (deal.photo_position || "center center") : (allPhotoObjects[activePhoto]?.position || "center center") }}
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-[#111111]/20" />
          <span className="stamp-corner top-6 right-6 md:top-10 md:right-12">RESTRICTED</span>

          {/* Photo counter */}
          {hasGallery && (
            <span
              className="absolute top-6 left-6 bg-[#111111]/75 text-white font-mono text-xs px-3 py-1.5 tracking-wider border border-[#3A3A3A]"
              data-testid="photo-counter"
            >
              {activePhoto + 1} / {allPhotos.length}
            </span>
          )}

          {/* Prev/next arrows — visible on all screen sizes when gallery exists */}
          {hasGallery && (
            <button
              type="button"
              onClick={prevPhoto}
              className="flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-[#111111]/75 hover:bg-[#111111]/95 items-center justify-center border border-[#3A3A3A] hover:border-[#555] transition-colors"
              aria-label="Previous photo"
              data-testid="photo-prev"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
          )}
          {hasGallery && (
            <button
              type="button"
              onClick={nextPhoto}
              className="flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-[#111111]/75 hover:bg-[#111111]/95 items-center justify-center border border-[#3A3A3A] hover:border-[#555] transition-colors"
              aria-label="Next photo"
              data-testid="photo-next"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          )}

          <div className="absolute inset-x-0 bottom-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12 md:pb-20 text-white">
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="rd-badge">{deal.tags?.[0] || "CLASSIFIED"}</span>
                {dealType === "flight" && deal.airline && (
                  <span className="rd-badge rd-badge-outline">{deal.airline.toUpperCase()}</span>
                )}
                {dealType === "hotel" && deal.star_rating && (
                  <span className="rd-badge rd-badge-outline">{deal.star_rating}★</span>
                )}
                {dealType === "cruise" && deal.ship_name && (
                  <span className="rd-badge rd-badge-outline">{deal.ship_name.toUpperCase()}</span>
                )}
                {dealType === "flight" && deal.cabin_class === "business" && (
                  <span className="rd-badge" style={{ background: "#C8922A", color: "white" }}>BUSINESS</span>
                )}
                {dealType === "flight" && deal.cabin_class === "first" && (
                  <span className="rd-badge" style={{ background: "#6C3FC5", color: "white" }}>FIRST</span>
                )}
              </div>
              <h1 className="font-mono text-5xl md:text-7xl leading-[0.95] tracking-tight uppercase">
                {dealType === "hotel" ? (
                  deal.hotel_name || deal.to_city
                ) : dealType === "cruise" ? (
                  cruiseH1
                ) : (isOpenJaw || (isMultiCity && returnFrom)) ? (
                  <>
                    {deal.from_city} <span className="text-[#CC1A1A]">→</span> {deal.to_city}
                    <span className="text-[#555]"> / </span>
                    {returnFrom} <span className="text-[#CC1A1A]">→</span> {returnTo}
                  </>
                ) : (
                  <>{deal.from_city} <span className="text-[#CC1A1A]">→</span> {deal.to_city}</>
                )}
              </h1>
              {heroSub && (
                <p className="mt-4 text-[#BEB3A6] text-lg font-mono tracking-wider uppercase">
                  {heroSub}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Photo credit — updates as user navigates the gallery */}
        {currentCredit && (
          <p style={{ fontFamily: "monospace", fontSize: "10px", color: "#444444", textAlign: "right", margin: "4px 8px 0 0" }}>
            {currentCredit}
          </p>
        )}

        {/* Gallery thumbnail strip */}
        {hasGallery && (
          <div
            className="flex gap-2 overflow-x-auto px-4 py-3 bg-[#0B0B0B] border-b border-[#1E1E1E]"
            data-testid="gallery-strip"
          >
            {allPhotos.map((url, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActivePhoto(idx)}
                className={`shrink-0 w-20 h-14 overflow-hidden border-2 transition-colors ${
                  idx === activePhoto
                    ? "border-[#CC1A1A]"
                    : "border-transparent hover:border-[#555]"
                }`}
                aria-label={`View photo ${idx + 1}`}
                data-testid={`gallery-thumb-${idx}`}
              >
                <img
                  src={url}
                  alt={`Photo ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </section>

      {deal.expired && (
        <div className="bg-[#CC1A1A] text-white text-center py-4 text-xs font-mono font-bold tracking-[0.18em] uppercase" data-testid="deal-expired-banner">
          // THIS DEAL HAS EXPIRED — prices may no longer be available
        </div>
      )}

      <main className="bg-[#111111]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24 grid md:grid-cols-3 gap-12">

          {/* Left: details */}
          <div className="md:col-span-2">
            {deal.teaser && (
              <p className="text-2xl md:text-3xl text-white leading-snug mb-10 font-sans italic" data-testid="deal-teaser">
                "{deal.teaser}"
              </p>
            )}

            <div className="hr-dot mb-10" />

            {/* ── Hotel details ── */}
            {dealType === "hotel" && (
              <div className="grid grid-cols-2 gap-y-8 gap-x-6">
                <Detail
                  label={deal.price_per_person ? "PRICE P.P./NIGHT" : "PRICE PER ROOM/NIGHT"}
                  value={`${s}${deal.price_per_person || deal.price_eur}`}
                  accent
                />
                <Detail label="HOTEL" value={deal.hotel_name} />
                <Detail label="LOCATION" value={[deal.to_city, deal.to_country].filter(Boolean).join(", ")} />
                <Detail label="STAR RATING" value={deal.star_rating ? `${deal.star_rating}★` : null} />
                <Detail label="BOARD TYPE" value={deal.board_type} />
                <Detail label="TRAVEL PERIOD" value={deal.checkin_period || deal.travel_window} icon={<Calendar className="w-4 h-4" />} />
              </div>
            )}

            {/* ── Cruise details ── */}
            {dealType === "cruise" && (
              <div className="grid grid-cols-2 gap-y-8 gap-x-6">
                <Detail label="PRICE FROM" value={`${s}${deal.price_eur} p.p.`} accent />
                <Detail label="CRUISE LINE" value={deal.cruise_line} />
                <Detail label="SHIP" value={deal.ship_name} />
                <Detail label="DEPARTURE PORT" value={deal.departure_port} />
                <Detail label="ROUTE" value={deal.cruise_route} />
                <Detail label="DESTINATION PORT" value={deal.destination_port} />
                <Detail label="DURATION" value={deal.duration_nights ? `${deal.duration_nights} nights` : null} />
                <Detail label="CABIN TYPE" value={deal.cabin_type} />
                <Detail label="TRAVEL WINDOW" value={deal.travel_window} icon={<Calendar className="w-4 h-4" />} />
              </div>
            )}

            {/* ── Package details ── */}
            {dealType === "package" && (
              <div className="grid grid-cols-2 gap-y-8 gap-x-6">
                <Detail label="PRICE PER PERSON" value={`${s}${deal.price_eur}`} accent />
                <Detail label="INCLUDES" value={deal.package_includes} />
                <Detail label="FROM" value={deal.from_city} />
                <Detail label="TO" value={deal.to_city} />
                <Detail label="TRAVEL WINDOW" value={deal.travel_window} icon={<Calendar className="w-4 h-4" />} />
                <Detail label="DURATION" value={deal.duration_nights ? `${deal.duration_nights} nights` : null} />
                <Detail label="AIRLINE" value={deal.airline} icon={<Plane className="w-4 h-4" />} />
                <Detail label="HOTEL" value={deal.hotel_name} />
              </div>
            )}

            {/* ── Flight details ── */}
            {(dealType === "flight" || !deal.deal_type) && (
              <div className="grid grid-cols-2 gap-y-8 gap-x-6">
                <Detail label="PRICE FROM" value={`${s}${deal.price_eur}`} accent />
                <Detail label="AIRLINE" value={deal.airline} />
                <Detail label="DEPARTURE" value={`${deal.from_city}${deal.from_country ? `, ${deal.from_country}` : ""}`} />
                <Detail label="DESTINATION" value={`${deal.to_city}${deal.to_country ? `, ${deal.to_country}` : ""}`} />
                {isOpenJaw && <Detail label="RETURN FROM" value={returnFrom} />}
                {isOpenJaw && <Detail label="RETURN TO" value={returnTo} />}
                {!isOpenJaw && deal.return_city && <Detail label="RETURN VIA" value={deal.return_city} />}
                <Detail label="TRAVEL WINDOW" value={deal.travel_window} icon={<Calendar className="w-4 h-4" />} />
                {deal.example_dates && <Detail label="EXAMPLE DATES" value={deal.example_dates} />}
                <Detail label="STOPS" value={formatStops(deal.stops)} icon={<Plane className="w-4 h-4" />} />
                <Detail label="BAGGAGE" value={deal.baggage ? deal.baggage.charAt(0).toUpperCase() + deal.baggage.slice(1) : deal.baggage} icon={<Luggage className="w-4 h-4" />} />
                {deal.cabin_class && (
                  <Detail label="CABIN CLASS" value={{ economy: "Economy", premium_economy: "Premium Economy", business: "Business", first: "First Class" }[deal.cabin_class] || deal.cabin_class} />
                )}
              </div>
            )}

            {/* Description */}
            {deal.description && (
              <>
                <div className="hr-dot my-12" />
                <p className="eyebrow eyebrow-red">// INTEL — {deal.hotel_name || deal.cruise_line || deal.to_city}</p>
                <div className="mt-4">
                  <RichTextRenderer content={deal.description} />
                </div>
              </>
            )}

            <div className="hr-dot my-12" />
            <p className="eyebrow mb-4">// LEAK THIS FILE</p>
            <ShareButtons deal={deal} />

            {/* eSIM affiliate block — only shown when there is a clear single destination */}
            {esimDestination && (
              <div className="mt-14 rd-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5" data-testid="esim-block">
                <div className="w-12 h-12 bg-[#FFDD57] text-[#111111] flex items-center justify-center shrink-0">
                  <Wifi className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="eyebrow eyebrow-red">// ON THE GROUND</p>
                  <p className="font-mono text-xl md:text-2xl text-white mt-1 tracking-tight uppercase">
                    Travelling to {esimDestination}?
                  </p>
                  <p className="text-sm text-[#9A8F83] mt-1 font-sans">
                    Skip the SIM queue — get a local eSIM before you land.
                  </p>
                </div>
                <a
                  href={TRIP_ESIM_URL}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="btn-rd-yellow shrink-0 w-full md:w-auto"
                  data-testid="esim-cta"
                >
                  GET A LOCAL eSIM FOR {esimDestination.toUpperCase()} →
                </a>
              </div>
            )}
          </div>

          {/* Right: CTA card */}
          <aside className="md:sticky md:top-24 h-fit rd-card-red p-8" data-testid="deal-cta-card">
            <p className="eyebrow eyebrow-red flex items-center gap-2">
              <Lock className="w-3 h-3" /> STARTING FROM
            </p>
            <p className="font-mono text-6xl text-white mt-2 tracking-tight">
              {s}{dealType === "hotel" && deal.price_per_person ? deal.price_per_person : deal.price_eur}
            </p>
            <p className="text-xs text-[#9A8F83] mt-1 font-mono uppercase tracking-wider">
              {priceLabel}
            </p>

            {(dealType === "cruise" || (dealType === "package" && !dealUrl)) ? (
              <div className="rd-card p-6 md:p-8 mt-8" data-testid="go-to-deal-btn">
                <p className="eyebrow eyebrow-red">
                  // BOOK THIS {dealType === "cruise" ? "CRUISE" : "PACKAGE"}
                </p>
                <p className="text-[#9A8F83] text-sm mt-2 mb-6 font-sans leading-relaxed">
                  {dealType === "cruise"
                    ? "Cruise bookings require personalised assistance. Contact us to check availability and secure your cabin at this price."
                    : "Package bookings require personalised assistance. Contact us to check availability and secure this deal at this price."}
                </p>
                <a
                  href="https://www.thebetterfare.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-rd w-full"
                >
                  ENQUIRE VIA THE BETTER FARE →
                </a>
                <a
                  href="mailto:hello@restricteddeals.com"
                  className="btn-rd-outline w-full mt-3"
                >
                  EMAIL US →
                </a>
                <a
                  href="https://wa.me/31648051678"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-rd-outline w-full mt-3"
                >
                  WHATSAPP US →
                </a>
                <p className="text-[11px] text-[#555] mt-4 font-mono tracking-wide uppercase">
                  Powered by The Better Fare · Atypical Agency
                </p>
              </div>
            ) : dealUrl ? (
              <>
                <a
                  href={clickUrl("deal")}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="btn-rd w-full mt-8"
                  data-testid="go-to-deal-btn"
                >
                  UNLOCK DEAL →
                </a>
                {dealType === "flight" && (
                  <a
                    href={tripFlightUrl}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="btn-rd-outline w-full mt-3"
                    data-testid="trip-compare-btn"
                  >
                    🔍 COMPARE PRICES ON TRIP.COM →
                  </a>
                )}
              </>
            ) : (
              <span className="btn-rd w-full mt-8 opacity-40 cursor-not-allowed text-center block" data-testid="go-to-deal-btn">
                DEAL LINK NOT SET
              </span>
            )}

            {/* Secondary CTA: hotels get flight search, flights get hotel search, packages get package search */}
            {!(dealType === "cruise" || (dealType === "package" && !dealUrl)) && (
              dealType === "hotel" ? (
                <a
                  href={FLIGHT_SEARCH_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-rd-outline w-full mt-3 flex items-center justify-center gap-2"
                  data-testid="flight-search-btn"
                >
                  <Plane className="w-4 h-4" /> SEARCH FLIGHTS TO {deal.to_city?.toUpperCase()} →
                </a>
              ) : dealType === "package" ? (
                <a
                  href={tripPackageUrl}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="btn-rd-outline w-full mt-3"
                  data-testid="trip-package-btn"
                >
                  📦 SEARCH PACKAGES ON TRIP.COM →
                </a>
              ) : dealType !== "cruise" ? (
                <a
                  href={deal.hotel_url ? clickUrl("hotel") : tripHotelUrl}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="btn-rd-yellow w-full mt-3"
                  data-testid="find-hotel-btn"
                >
                  <Hotel className="w-4 h-4" /> FIND A HOTEL IN {deal.to_city?.toUpperCase()} →
                </a>
              ) : null
            )}

            {!(dealType === "cruise" || (dealType === "package" && !dealUrl)) && (
              <p className="text-[11px] text-[#9A8F83] mt-6 leading-relaxed flex items-start gap-2 font-mono tracking-wide uppercase">
                <ArrowUpRight className="w-3 h-3 shrink-0 mt-0.5" />
                External partner · prices change fast · verify before payment
              </p>
            )}
            <div className="hr-dot my-8" />
            <div className="flex items-center gap-2 text-[11px] text-[#666] font-mono tracking-wider uppercase">
              <Calendar className="w-3 h-3" />
              POSTED {deal.created_at ? new Date(deal.created_at).toLocaleDateString("en-GB") : "—"}
            </div>

            <div className="mt-4 pt-4 border-t border-[#1E1E1E]">
              <p className="font-mono text-[9px] text-[#333] tracking-[0.2em] uppercase mb-2">// BOOK VIA PARTNER</p>
              <a
                href="https://www.trip.com/?Allianceid=4012514&SID=308550495&trip_sub1=&trip_sub3=D16678678"
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="btn-rd-outline w-full text-center"
              >
                BOOK ON TRIP.COM →
              </a>

              {dealType === "flight" && (
                <>
                  <p style={{ fontFamily: "monospace", fontSize: "9px", color: "#444", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: "16px", marginBottom: "4px" }}>
                    // PASSENGER RIGHTS
                  </p>
                  <a
                    href="https://airhelp.tp.st/GQN5CHCB"
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="btn-rd-outline w-full mt-3"
                    style={{ fontSize: "11px" }}
                  >
                    ✈ FLIGHT DELAYED OR CANCELLED? CLAIM UP TO €600 →
                  </a>
                </>
              )}

              {transferCity && (
                <>
                  <p style={{ fontFamily: "monospace", fontSize: "9px", color: "#444", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: "8px", marginBottom: "4px" }}>
                    // GROUND TRANSPORT
                  </p>
                  <a
                    href="https://tp.st/BmcHG6Be"
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="btn-rd-outline w-full mt-3"
                    style={{ fontSize: "11px" }}
                  >
                    🚗 AIRPORT TRANSFER AT {transferCity.toUpperCase()} →
                  </a>
                </>
              )}
            </div>
          </aside>
        </div>
      </main>

      {related.length > 0 && (
        <section className="border-t border-[#2A2A2A] bg-[#0B0B0B] py-14" data-testid="related-deals">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <p className="eyebrow eyebrow-red mb-6">// MORE RESTRICTED DEALS</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((d) => {
                const rs = CURRENCY_SYMBOLS[d.price_currency] || "€";
                const relSlug = d.slug || d.id;
                const relTitle = d.deal_type === "hotel"
                  ? (d.hotel_name || d.to_city)
                  : d.deal_type === "cruise"
                  ? (d.cruise_line || d.cruise_route || d.to_city)
                  : `${d.from_city || ""} → ${d.to_city || ""}`;
                return (
                  <Link
                    key={d.id}
                    to={`/deal/${relSlug}`}
                    className="group block bg-[#111111] border border-[#2A2A2A] hover:border-[#CC1A1A] transition-colors"
                    data-testid={`related-deal-${d.id}`}
                  >
                    <div style={{ height: "120px", overflow: "hidden", position: "relative" }}>
                      <img
                        src={d.image_url || "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?w=800&q=70&auto=format&fit=crop"}
                        alt={relTitle}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
                      />
                    </div>
                    <div className="px-4 py-3 flex items-start justify-between gap-2">
                      <p className="font-mono text-xs text-white uppercase tracking-wide leading-tight group-hover:text-[#CC1A1A] transition-colors">
                        {relTitle}
                      </p>
                      <span className="rd-badge rd-badge-red shrink-0 !text-[0.6rem]">
                        {rs}{d.price_eur}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {dealRegion && (
        <div className="border-t border-[#2A2A2A] bg-[#0B0B0B] py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <Link
              to={`/?region=${encodeURIComponent(dealRegion)}`}
              className="eyebrow eyebrow-red hover:text-white transition-colors inline-flex items-center gap-2"
            >
              // MORE {dealRegion.toUpperCase()} DEALS →
            </Link>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}

function Detail({ label, value, icon, accent }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <p className="eyebrow flex items-center gap-2">{icon}{label}</p>
      <p className={`font-mono text-2xl mt-1 tracking-tight ${accent ? "text-[#CC1A1A]" : "text-white"}`}>
        {value}
      </p>
    </div>
  );
}
